import { Checkbox, Tooltip } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { AppContext, License } from 'jsx';
import React, { Dispatch, SetStateAction, useContext } from 'react';
import { Info } from 'react-feather';
import { useTranslation } from 'react-i18next';
import {
  PermissionSetOption,
  PermissionSetOptionGroups,
  PermissionSetRow,
  UserPermissionsAPI,
} from '../util/permissionsForEdit';
import { Cell, Row } from './Table';

export const CHECKBOX_COLUMN_WIDTH = 200;

type Props = {
  perSet: PermissionSetRow;
  userPermissions: UserPermissionsAPI[];
  checkedList: (string | undefined)[];
  setCheckedList: Dispatch<SetStateAction<(string | undefined)[]>>;
  optionsWithViewPermission: PermissionSetOption[];
  disabled?: boolean;
  license: License;
  handleChange: (
    e: CheckboxChangeEvent,
    userPermission: UserPermissionsAPI | undefined,
    userPermissions: UserPermissionsAPI[],
    permissionId: number,
    permissionObject: PermissionSetOption
  ) => void;
};

export default function PermissionRow({
  perSet,
  userPermissions,
  checkedList,
  setCheckedList,
  optionsWithViewPermission,
  disabled,
  license,
  handleChange,
}: Props) {
  const { allPermissionsMap } = useContext(AppContext);
  const [t] = useTranslation(['authorization', 'common']);

  return (
    <Row>
      <Cell style={{ paddingLeft: 16 }}>{perSet.label}</Cell>
      {perSet.options.map((option: PermissionSetOption, i: number) => {
        if (!option?.codename) {
          return <Cell key={`empty${i}`} width={CHECKBOX_COLUMN_WIDTH}></Cell>;
        }
        const permission = allPermissionsMap.get(option.codename);

        const userPermission = userPermissions.find(
          (x: any) =>
            x.codename === option.codename &&
            (!x.object_id || x.object_id === perSet.object_id)
        );

        const isInheritedFromGroup = userPermission
          ? userPermission.groups?.length > 0
          : false;
        const isInherited = isInheritedFromGroup || option.isInherited === true;

        const isChecked = checkedList.find(
          (item: string | undefined) => item === option.codename
        );

        const isViewDisabled =
          optionsWithViewPermission.find((opt: any) => {
            if (Array.isArray(opt.view)) {
              return opt.view.includes(option.codename);
            }
            return opt.view === option.codename;
          }) && checkedList.includes(option.codename);

        const isLicenseDisabled =
          license && !(option.license_codename! in license);

        return (
          <Cell
            key={option.codename}
            width={CHECKBOX_COLUMN_WIDTH}
            style={{ justifyContent: 'center' }}
          >
            <div style={{ position: 'relative' }}>
              <Checkbox
                data-ta={option.codename}
                checked={!!isChecked || option.isInherited}
                disabled={
                  isInherited ||
                  disabled ||
                  option.disabled ||
                  !!isViewDisabled ||
                  !!isLicenseDisabled
                }
                key={option.codename}
                onChange={e => {
                  if (e.target.checked) {
                    if (option.view) {
                      // In case there are several view permissions to check
                      const viewOption = Array.isArray(option.view)
                        ? [...option.view]
                        : [option.view];
                      setCheckedList([
                        ...checkedList,
                        ...viewOption,
                        option.codename,
                      ]);
                    } else {
                      setCheckedList([...checkedList, option.codename]);
                    }
                  } else {
                    const filteredCheckList = checkedList.filter(
                      item => item !== option.codename
                    );
                    setCheckedList(filteredCheckList);
                  }
                  if (permission)
                    handleChange(
                      e,
                      userPermission,
                      userPermissions,
                      permission,
                      option
                    );
                }}
              />
              {(isInherited || isInheritedFromGroup || isLicenseDisabled) && (
                <div style={{ position: 'absolute', left: 24, top: 0 }}>
                  <Tooltip
                    title={
                      <>
                        {isInheritedFromGroup ? (
                          <span style={{ fontSize: 12 }}>
                            {t('This permission is inherited from the')}{' '}
                            {userPermission?.groups.length === 1
                              ? t('group')
                              : t('groups')}{' '}
                            {userPermission?.groups
                              .map(
                                (
                                  a: PermissionSetOptionGroups,
                                  index: number
                                ) =>
                                  index === userPermission?.groups.length - 1 &&
                                  index > 0
                                    ? t("and '(NAME)'", { groupName: a.name })
                                    : `'${a.name}'`
                              )
                              .join(', ')
                              .replace(', and', ' and')}
                          </span>
                        ) : (
                          ''
                        )}

                        {isInherited && !isInheritedFromGroup ? (
                          <span style={{ fontSize: 12, lineHeight: 1 }}>
                            {t(
                              "authorization:This permission is inherited from this user's global permissions"
                            )}
                          </span>
                        ) : (
                          ''
                        )}

                        {isLicenseDisabled && (
                          <div style={{ fontSize: 12 }}>
                            {t(
                              'This module is not included in current license'
                            )}
                          </div>
                        )}
                      </>
                    }
                  >
                    <Info size={15} className="text-gray-400" />
                  </Tooltip>
                </div>
              )}
            </div>
          </Cell>
        );
      })}
    </Row>
  );
}
